import { BottomSheetModal } from '@gorhom/bottom-sheet';
import { InlineAlertType, Typenames } from '@varo/graphql-schema-types';
import useAppVisibility from 'features/chat/hooks/useAppVisibility';
import MultipleContacts from 'features/v2a/sender/components/MultipleContacts';
import RecipientListItem from 'features/v2a/sender/components/RecipientListItem';
import {
  AllLocalRecipients,
  AllRecipientAndLocalRecipient,
  AllRecipients,
  displayInfo,
  EXTERNAL_USER,
  getAvatarInitials,
  getAvatarTheme,
  getFilteredRecipients,
  getIsEmail,
  getIsPhone,
  hasMultipleContacts,
  recipientAddresses,
  shouldUseInitialsForAvatar,
} from 'features/v2a/sender/helpers/Sender.helpers';
import { useLocalContacts } from 'features/v2a/sender/hooks/useLocalContacts';
import useRecipients from 'features/v2a/sender/hooks/useRecipients';
import useRequestContactPermission from 'features/v2a/sender/hooks/useRequestContactPermission';
import { V2AContext } from 'features/v2a/sender/provider/V2AContextProvider';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Linking, SectionList, StyleSheet, View } from 'react-native';
import AmplitudeMask from 'shared/components/AmplitudeMask/AmplitudeMask';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import { OnboardingCard } from 'shared/components/vxl/cards/OnboardingCard/OnboardingCard';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import SectionDivider from 'shared/components/vxl/divider/SectionDivider/SectionDivider';
import { InlineAlert } from 'shared/components/vxl/InlineAlert/InlineAlert';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import BodyBaseBoldText from 'shared/components/vxl/typography/primary/body/BodyBaseBoldText';
import { isIOS, isWeb } from 'shared/constants/platform';
import { useAnalyticsConfiguration } from 'shared/hooks/analytics/useAnalyticsConfiguration';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { formatPhoneNumber } from 'shared/utils/string/phone';

import EmptyRecipientList from './EmptyRecipientList/EmptyRecipientList';

interface RecipientListProp {
  searchPhrase: string;
}

const RecipientList = ({ searchPhrase }: RecipientListProp) => {
  const {
    contacts: localContacts,
    hasPermission,
    canAskAgain,
    isLoading,
    error,
    fetchContacts,
  } = useLocalContacts();

  const { setSelectedRecipient } = useContext(V2AContext);

  const [isDataReady, setIsDataReady] = useState(false);

  const {
    recipients: topRecipients,
    V2ARecipientsError,
    V2ARecipientsLoading,
  } = useRecipients();

  const [filteredLocalContacts, setFilteredLocalContacts] =
    useState<AllLocalRecipients[]>(localContacts);

  const [filteredRecipients, setFilteredRecipients] = useState<AllRecipients[]>(
    topRecipients ?? [],
  );

  const [pendingRecipient, setPendingRecipient] =
    useState<AllRecipientAndLocalRecipient>();

  const [bottomSheetOptions, setBottomSheetOptions] = useState<string[]>([]);
  const { permissionStatus, requestPermission } = useRequestContactPermission();

  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'sender',
  });

  const bottomSheetRef = useRef<BottomSheetModal>(null);
  const screenEventLogged = useRef(false);
  const { segmentClient } = useAnalyticsConfiguration();
  const isHidden = useAppVisibility(); // need to use this hook, because the useFocusEffect not worked

  const handleAllowContactAccessPress = () => {
    if (canAskAgain && hasPermission === false && isIOS) {
      void requestPermission();
    } else {
      Linking.openSettings().catch(() => {});
    }
  };

  useEffect(() => {
    if (!isHidden || isIOS || permissionStatus === 'granted') {
      void fetchContacts();
    }
  }, [fetchContacts, isHidden, permissionStatus]);

  useEffect(() => {
    if (topRecipients && !screenEventLogged.current) {
      void segmentClient.logScreenEvent('V2A Search Recipient', {
        name: 'V2A Search Recipient',
        category: 'v2a',
        subcategory: 'transfer_money_v2a_sender',
        top_recipient_count: topRecipients.length,
      });
      screenEventLogged.current = true;
    }
  }, [segmentClient, topRecipients]);

  useEffect(() => {
    if (!topRecipients) {
      return;
    }
    const updateFilteredContacts = () => {
      const filteredLocal =
        (getFilteredRecipients(
          localContacts,
          searchPhrase,
        ) as AllLocalRecipients[]) ?? [];

      const filteredRecipients =
        (getFilteredRecipients(
          topRecipients,
          searchPhrase,
        ) as AllRecipients[]) ?? [];

      setFilteredLocalContacts(filteredLocal);
      setFilteredRecipients(filteredRecipients);
    };

    updateFilteredContacts();
    setIsDataReady(true);
  }, [localContacts, topRecipients, searchPhrase]);

  const sections: Array<{
    title: string;
    data: AllRecipientAndLocalRecipient[];
  }> = [
    {
      title: translatedText('recipients.topRecipients'),
      data: filteredRecipients,
    },
    {
      title: translatedText('recipients.allRecipients'),
      data: filteredLocalContacts,
    },
  ].filter((section) => section.data.length > 0);

  const contactsAllowedEvent = usePressableAnalytics({
    analyticsId: 'educationScreenButton',
    customAttributes: { source_name: 'v2a_search_recipient' },
  });

  const onOptionSelected = (data: string) => {
    if (!data) {
      return;
    }

    const updatedRecipient = {
      ...pendingRecipient,
      email: getIsEmail(data) ? { address: [data], verified: true } : undefined,
      phoneNumber: getIsPhone(data) ? data : undefined,
    } as AllLocalRecipients | AllRecipients;
    setSelectedRecipient(updatedRecipient);
    bottomSheetRef.current?.close();
  };

  const logContactSelectedEvent = (contactType?: string | null) => {
    void segmentClient.logEvent('V2A Receiver Contact Selected', {
      source_name: 'v2a_select_contact',
      contact_type: contactType,
      category: 'v2a',
      subcategory: 'transfer_money_v2a_sender',
    });
  };

  const handleRecipientPress = (item: AllRecipientAndLocalRecipient) => {
    if (hasMultipleContacts(item)) {
      const options = recipientAddresses(item);
      setBottomSheetOptions(options);
      bottomSheetRef.current?.present();
      setPendingRecipient(item);
      logContactSelectedEvent(null); // explicitly passed null because its yet to select phone|email
    } else {
      setSelectedRecipient(item);
      let contactType: string | null = null;
      if (item?.email?.address) {
        contactType = 'email';
      } else if (item?.phoneNumber) {
        contactType = 'phone';
      }
      logContactSelectedEvent(contactType);
    }
  };

  if (
    !isLoading &&
    !V2ARecipientsLoading &&
    !filteredLocalContacts.length &&
    !filteredRecipients.length
  ) {
    const isEmail = getIsEmail(searchPhrase);
    const isPhone = getIsPhone(searchPhrase);

    if (isEmail || isPhone) {
      const newContact: AllRecipients = {
        id: EXTERNAL_USER,
        displayName: searchPhrase,
        email: { address: isEmail ? searchPhrase : '', verified: true },
        phoneNumber: isPhone ? searchPhrase : '',
        __typename: Typenames.V2AExternalRecipient,
      };

      return (
        <>
          <SectionDivider
            labelText={translatedText('recipients.newRecipients')}
          />
          <AmplitudeMask>
            <RecipientListItem
              id={newContact.id}
              title={
                isPhone
                  ? formatPhoneNumber(newContact.displayName, false)
                  : newContact.displayName
              }
              subTitle={
                newContact.id !== EXTERNAL_USER ? displayInfo(newContact) : null
              }
              avatarTheme={
                shouldUseInitialsForAvatar(newContact)
                  ? getAvatarTheme(getAvatarInitials(newContact))
                  : undefined
              }
              showVaroLogo={false}
              onItemPress={() => handleRecipientPress(newContact)}
            />
          </AmplitudeMask>
        </>
      );
    }
  }

  return (
    <View style={styles.container}>
      <ScreenTransitionOverlay
        isLoading={isLoading || V2ARecipientsLoading}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error && V2ARecipientsError ? (
            <InlineAlert
              alertType={InlineAlertType.Error}
              bodyText={
                error || translatedText('recipients.errorFetchingContacts')
              }
            />
          ) : null
        }
      />

      <SectionList
        sections={sections}
        ListEmptyComponent={
          !isLoading && isDataReady && sections.length === 0 ? (
            <EmptyRecipientList />
          ) : null
        }
        renderItem={({
          item,
        }: {
          item: AllRecipients | AllLocalRecipients;
        }) => (
          <RecipientListItem
            id={item.id}
            title={item.displayName}
            subTitle={item.id !== EXTERNAL_USER ? displayInfo(item) : null}
            avatarTheme={
              shouldUseInitialsForAvatar(item.displayName)
                ? getAvatarTheme(getAvatarInitials(item.displayName))
                : undefined
            }
            showVaroLogo={item.__typename === Typenames.V2AInternalRecipient}
            onItemPress={() => handleRecipientPress(item)}
          />
        )}
        keyExtractor={(item, index) => (item.id ? item.id : `${index}`)}
        ItemSeparatorComponent={() => (
          <View style={styles.separatorStyle}>
            <HairLineDivider />
          </View>
        )}
        stickySectionHeadersEnabled={false}
        renderSectionHeader={({ section: section }) => {
          return section.data.length > 0 ? (
            <BodyBaseBoldText style={styles.sectionHeader} numberOfLines={1}>
              {section.title}
            </BodyBaseBoldText>
          ) : null;
        }}
        ListFooterComponent={
          !hasPermission && !isWeb ? (
            <View style={styles.permissionCard}>
              <OnboardingCard
                actionText={translatedText(
                  'recipients.contactAccessBannerAction',
                )}
                bodyText={translatedText(
                  'recipients.contactAccessBannerMessage',
                )}
                illustration="HIGH_FIVE_A"
                onPress={() => {
                  contactsAllowedEvent();
                  handleAllowContactAccessPress();
                }}
                showBorder
              />
            </View>
          ) : null
        }
      />
      <BottomSheet ref={bottomSheetRef}>
        <MultipleContacts
          contacts={bottomSheetOptions}
          setChosenContact={onOptionSelected}
        />
      </BottomSheet>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },

  permissionCard: {
    marginVertical: theme.spacing.m,
    margin: theme.spacing.m,
  },
  sectionHeader: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  separatorStyle: {
    marginHorizontal: theme.spacing.m,
  },
});

export default RecipientList;
